import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { createRef } from 'react'

import draft from '../../../lib/draft'
import { DiscardDialogContext } from '../../../lib/global-discard-dialog'

import { removeBlanks, removeBlanksKeepArray } from './UtilityFunctions'
import { Validators } from './Validators'

export const makeValidatedInput = inputComp =>
  class ValidatedInput extends React.Component {
    static contextTypes = {
      form: PropTypes.instanceOf(Object).isRequired
    }

    UNSAFE_componentWillMount () {
      this.setState({ touched: false })
    }

    componentDidMount () {
      this.registerValidatorsInForm()
    }

    componentDidUpdate () {
      this.registerValidatorsInForm()
    }

    componentWillUnmount () {
      this.deregisterValidatorsInForm()
    }

    registerValidatorsInForm = () => {
      const { form } = this.context
      const { inputPath, validators } = this.props

      form.addValidators(
        inputPath,
        validators || this.getSuggestedValidators()
      )
    }

    deregisterValidatorsInForm = () => {
      const { form } = this.context
      const { inputPath } = this.props

      form.removeValidators(inputPath)
    }

    getSuggestedValidators = () => {
      const arr = []
      if (this.props.required) {
        arr.push(Validators.required())
      }
      if (this.props.phone) {
        arr.push(Validators.phone())
      }
      return arr
    }

    handleBlur = e => {
      const { onBlur } = this.props
      const { form } = this.context

      onBlur && onBlur(e)
      form.validate()

      this.setState({ touched: true })
    }

    render () {
      const { form } = this.context
      const { inputPath } = this.props
      const errors = form.getInputErrors(inputPath)
      return React.createElement(inputComp, {
        ...this.props,
        errors: this.state.touched || form.showAllErrors ? errors : [],
        onBlur: this.handleBlur
      })
    }
  }

export const makeAutobindedInput = inputComp =>
  class AutobindedInput extends React.Component {
    static contextTypes = {
      form: PropTypes.instanceOf(Object).isRequired,
      parentInputPath: PropTypes.string
    }

    constructor (props) {
      super(props)
      this.ref = props.fileRef || createRef()
    }

    /* shouldComponentUpdate(nextProps, nextState) {
     *   const nextBindedProps = this.getBindedProps(nextProps.name);
     *   const result =
     *     this.pureRenderScu(nextProps, nextState) ||
     *     !_.isEqual(nextBindedProps.value, this.bindedProps.value);

     *   this.bindedProps = nextBindedProps;

     *   return result;
     * }
     */

    canHaveRef () {
      return Object.getPrototypeOf(inputComp) === React.Component
    }

    render () {
      let refAttr = {}
      const { name } = this.props

      if (this.canHaveRef()) {
        refAttr = { ref: this.ref }
      }

      const pathComponent = _.isInteger(name) ? `[${name}]` : `.${name}`

      const inputPath = (this.context.parentInputPath || '') + pathComponent

      const { form } = this.context

      if (form) {
        return React.createElement(inputComp, {
          ...this.props,
          ...refAttr,
          inputPath,
          name,
          value: form.getInputValue(inputPath),
          onChange: v => {
            this.props.onChange?.(v)
            form.setInputValue(inputPath, v)
          }
        })
      }

      return (
        <strong>
          Error: no form in React context
        </strong>
      )
    }
  }

export const bindTargetMixin = (base, defaultValue) => {
  class BoundTarget extends base {
    static childContextTypes = {
      form: PropTypes.instanceOf(Object),
      parentInputPath: PropTypes.string,
      formKey: PropTypes.string
    }

    static contextType = DiscardDialogContext

    constructor (props) {
      super(props)

      this.inputValidators = {}

      let value = props.value ??
        props.initialValues ??
        defaultValue ??
        {}

      this.state = {
        value: _.cloneDeep(value),
        initialValues: _.cloneDeep(value),
        errors: null,
        ignoreDiscardDialog: false
      }
    }

    componentDidMount () {
      this.validate(false)

      if (this.props.draft) {
        this.setState({ draft: { getting: true } })
        draft
          .get(this.props.draft)
          .then(({ data }) => {
            this.setState({
              draft: {
                get: true,
                data
              }
            })
            if (data) {
              this.setState({ value: data }, () => {
                this.validate()
              })
            }
          })
          .catch(error => {
            this.setState({ draft: { error } })
          })
      }
    }

    getChildContext = () => ({
      form: this,
      parentInputPath: '',
      formKey: this.props.formKey
    })

    getValue = () => {
      const { value } = this.state
      return { ...value }
    }

    getInputValue = inputPath => _.get(this.state, `value${inputPath}`)

    getInputErrors = inputPath => _.get(this.state, `errors${inputPath}`)

    setInputErrors = (inputPath, errors) => {
      this.setState(prevState =>
        _.set(prevState, `errors${inputPath}`, errors))
    }

    addValidators = (inputPath, validators) => {
      this.inputValidators[inputPath] = validators
    }

    removeValidators = inputPath => {
      delete this.inputValidators[inputPath]
    }

    validateInput = inputPath => {
      const validators = this.inputValidators[inputPath]

      return _.compact(
        _.map(validators, v =>
          v(this.getInputValue(inputPath), this.state.value))
      )
    }

    validate = (forceToShowAllErrors = true) => {
      let newErrors = { errors: {} }

      Object.keys(this.inputValidators).forEach(inputPath => {
        const inputErrors = this.validateInput(inputPath)

        newErrors = _.set(newErrors, `errors${inputPath}`, inputErrors)
      })

      this.showAllErrors = this.showAllErrors || forceToShowAllErrors
      const cleanedNewErrors = removeBlanksKeepArray(newErrors.errors)

      this.setState({ errors: cleanedNewErrors })

      return _.isNil(cleanedNewErrors) || _.isEqual(cleanedNewErrors, {})
    }

    isValid = () => !this.state.errors

    setInputValue = (path, value, callback) => {
      let { onChange, noGlobal } = this.props
      let discardDialog = this.context

      if (Array.isArray(this.state.value)) {
        throw new Error('Not implemented yet')
      }

      this.setState(
        // State setter
        prevState => ({
          value: _.set(
            _.cloneDeep(prevState.value ?? {}),
            path.replace(/^\./, ''),
            value
          )
        }),

        // setState callback
        () => {
          let newValue = removeBlanks(this.state.value)
          let initialValue = removeBlanks(this.state.initialValues)
          let isDirty = !_.isEqual(newValue, initialValue)
          let { ignoreDiscardDialog } = this.state

          if (!noGlobal) {
            if (
              isDirty &&
              !ignoreDiscardDialog &&
              typeof discardDialog?.onFormChange === 'function'
            ) {
              discardDialog.onFormChange(this.props.formKey)
              this.forceUpdate()
            }

            if (
              !isDirty &&
              typeof discardDialog?.reset === 'function'
            ) {
              discardDialog.resetForm(this.props.formKey)
              this.forceUpdate()
            }
          }

          onChange?.(this.state.value)
          callback?.()

          if (this.props.draft) {
            let { resource, path, user } = this.props.draft

            let params = {
              resource,
              path,
              user,
              data: this.state.value
            }

            this.setState({
              draft: {
                setting: true
              }
            })

            draft
              .set(params)
              .then(({ data }) => {
                this.setState({
                  draft: {
                    set: true,
                    data
                  }
                })
              })
              .catch(error => {
                this.setState({
                  draft: {
                    error
                  }
                })
              })
          }

          this.validate()
        })
    }
  }

  return BoundTarget
}

class PlainCollection extends React.Component {
  static childContextTypes = {
    parentInputPath: PropTypes.string
  }

  getChildContext () {
    return {
      parentInputPath: this.props.inputPath
    }
  }

  handleAdd = () => {
    const newItems = this.getItems().concat([ this.props.newItemValue || {} ])

    if (this.props.onChange) {
      this.props.onChange(newItems)
    }
  }

  handleRemove = index => {
    const newItems = [ ...this.getItems() ]
    newItems.splice(index, 1)

    if (this.props.onChange) {
      this.props.onChange(newItems)
    }
  }

  getItems = () => this.props.value || []

  render () {
    const {
      disableAdd,
      disableRemove,
      allowEmpty,
      className,
      filterFn,
      disableRemoveFn,
      addStyleFn
    } = this.props

    if (React.Children.count(this.props.children) > 1) {
      console.warn('Only one child is allowed for Collection component')
    }

    const childEl = React.Children.only(this.props.children)
    const items = this.getItems()

    const filter = filterFn || (i => i)

    const renderedItems = items
      .filter(i => filter(i))
      .map((item, itemIndex) => {
        const isLastItem = itemIndex === items.length - 1
        const disableRm = disableRemoveFn
          ? disableRemoveFn(item)
          : disableRemove

        return (
          <div
            key={itemIndex}
            className={`collection-item ${
              addStyleFn ? addStyleFn(itemIndex) : ''
            }`}
          >
            <div className='btn-wrapper'>
              {!disableAdd && isLastItem && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <span
                  className='add-btn icon icon-circle-add'
                  onClick={this.handleAdd}
                />
              )}
              {(
                (allowEmpty && !disableRm) ||
                (!disableRm && items.length > 1)
              ) && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <span
                  className='remove-btn icon icon-circle-subtract'
                  onClick={() => this.handleRemove(itemIndex)}
                />
              )}
            </div>
            {React.cloneElement(childEl, {
              name: itemIndex
            })}
          </div>
        )
      })

    return (
      <div className={`collection-input ${className || ''}`}>
        {renderedItems}
      </div>
    )
  }
}

export const Collection = makeAutobindedInput(PlainCollection)

class PlainFieldset extends React.Component {
  getChildContext = () => ({
    parentInputPath: this.props.inputPath
  })

  render () {
    return <div className='fieldset'>{this.props.children}</div>
  }
}

PlainFieldset.childContextTypes = {
  parentInputPath: PropTypes.string
}

export const Fieldset = makeAutobindedInput(PlainFieldset)
